import { useCallback, useEffect, useMemo, useState } from 'react';

export const DEFAULT_STAGE_SCALE = 0.3;
const MIN_STAGE_SCALE = 0.1;
const MAX_STAGE_SCALE = 2;
const VIEWPORT_HORIZONTAL_PADDING = 48;

/**
 * Owns the viewport-specific state for the page editor's Konva stage.
 *
 * The callback ref deliberately stores the DOM element in state. This makes
 * the ResizeObserver reconnect correctly when React replaces the viewport.
 */
export function useEditorViewport(canvasSize) {
  const [canvasViewportElement, setCanvasViewportElement] = useState(null);
  const [canvasViewportWidth, setCanvasViewportWidth] = useState(0);
  const [stageScale, setStageScale] = useState(DEFAULT_STAGE_SCALE);
  const [showGrid, setShowGrid] = useState(false);

  const canvasViewportRef = useCallback((element) => {
    setCanvasViewportElement(element);
  }, []);

  // Keep the complete page visible in narrower editor columns. The requested
  // zoom remains intact, but the display may use a smaller fit-to-width scale
  // instead of clipping the right-hand side of the Konva stage.
  const effectiveStageScale = useMemo(() => {
    if (!canvasViewportWidth || !canvasSize?.width) return stageScale;
    const fitScale = Math.max(
      0.05,
      (canvasViewportWidth - VIEWPORT_HORIZONTAL_PADDING) / canvasSize.width,
    );
    return Math.min(stageScale, fitScale);
  }, [canvasSize?.width, canvasViewportWidth, stageScale]);

  useEffect(() => {
    if (!canvasViewportElement || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const observer = new ResizeObserver(([entry]) => {
      setCanvasViewportWidth(Math.round(entry.contentRect.width));
    });
    observer.observe(canvasViewportElement);
    return () => observer.disconnect();
  }, [canvasViewportElement]);

  const handleZoomIn = useCallback(() => {
    setStageScale((previousScale) => Math.min(previousScale + 0.1, MAX_STAGE_SCALE));
  }, []);

  const handleZoomOut = useCallback(() => {
    setStageScale((previousScale) => Math.max(previousScale - 0.1, MIN_STAGE_SCALE));
  }, []);

  const handleResetZoom = useCallback(() => {
    setStageScale(DEFAULT_STAGE_SCALE);
  }, []);

  return {
    canvasViewportElement,
    canvasViewportRef,
    canvasViewportWidth,
    effectiveStageScale,
    handleResetZoom,
    handleZoomIn,
    handleZoomOut,
    setShowGrid,
    setStageScale,
    showGrid,
    stageScale,
  };
}
